import React from "react";

const formatKey = (key) =>
  key
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (str) => str.toUpperCase())
    .replace(/_/g, " ");

// Map AI-specific keys to friendly labels. Handles variants like TopInsight1, Top_Insight_1, Top Insight1
const mapAIKey = (rawKey) => {
  if (!rawKey) return '';
  // normalize spacing and camelCase
  const normalized = rawKey.replace(/[_-]/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').trim();
  // match Top Insight patterns with optional spaces/underscores and a trailing number
  const m = normalized.match(/^Top\s*Insight\s*\.?\s*(\d+)$/i) || normalized.match(/^Top\s*Insight(\d+)$/i);
  if (m) {
    const idx = m[1];
    return `Health recommendation ${idx}`;
  }
  // default to the existing formatter
  return formatKey(rawKey);
};

// Map common AI abbreviation values to human-readable phrases
const mapAIValue = (val) => {
  if (typeof val !== 'string') return val;
  const v = val.trim();
  // exact abbreviation mappings (case-insensitive)
  if (/^WHeightR$/i.test(v) || /^WaistHeightRatio$/i.test(v)) return 'Waist to Height Ratio';
  if (/^WHipR$/i.test(v) || /^WaistHipRatio$/i.test(v)) return 'Waist to Hip Ratio';
  if (/^WC$/i.test(v)) return 'Waist Circumference';
  if (/^SIE$/i.test(v)) return 'Stress Interest Energy';
  if (/^BP$/i.test(v)) return 'Blood Pressure';
  if (/^RestingHR$/i.test(v) || /^RestingHRate$/i.test(v)) return 'Resting heart rate';
  if (/^Bglucose$/i.test(v)) return 'Blood Glucose';
  // keep BMI as-is per your example; optionally expand to 'Body Mass Index' if desired
  return v;
};

const isColorCode = (value) =>
  typeof value === "string" &&
  /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(value);

// Return black or white depending on perceived brightness for contrast
const contrastTextColor = (hex) => {
  if (!hex || typeof hex !== 'string') return '#000';
  let h = hex.replace('#', '');
  if (h.length === 3) {
    h = h.split('').map(c => c + c).join('');
  }
  const r = parseInt(h.substr(0,2), 16);
  const g = parseInt(h.substr(2,2), 16);
  const b = parseInt(h.substr(4,2), 16);
  // YIQ brightness
  const yiq = (r*299 + g*587 + b*114) / 1000;
  return yiq >= 128 ? '#000' : '#fff';
};

// Simple color->label map used in several places in the app
const colorLabelMap = {
  '#5BC0DE': 'Good',
  '#ffe366': 'Good',
  '#ff944d': 'Fair',
  '#ff6666': 'High Risk',
  '#d9534f': 'Critical'
};

const labelForColor = (hex) => {
  if (!hex) return '';
  const upper = hex.toUpperCase();
  if (colorLabelMap[upper]) return colorLabelMap[upper];
  // fallback: return a basic color name (Green, Yellow, Orange, Red, Blue, Purple, Gray)
  try {
    const name = colorNameFromHex(hex);
    return name;
  } catch (e) {
    return hex;
  }
};

// Helpers to map hex -> RGB -> HSL -> basic color name
const hexToRgb = (hex) => {
  if (!hex) return null;
  let h = hex.replace('#', '').trim();
  if (h.length === 3) h = h.split('').map(c => c + c).join('');
  if (h.length !== 6) return null;
  const r = parseInt(h.substr(0,2), 16);
  const g = parseInt(h.substr(2,2), 16);
  const b = parseInt(h.substr(4,2), 16);
  return { r, g, b };
};

const rgbToHsl = ({ r, g, b }) => {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0, l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }
  return { h: h * 360, s: s * 100, l: l * 100 };
};

const colorNameFromHex = (hex) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  const { h, s, l } = rgbToHsl(rgb);
  // low saturation -> gray/white/black
  if (s < 8) {
    if (l > 90) return 'White';
    if (l < 10) return 'Black';
    return 'Gray';
  }
  // map hue ranges to basic color names
  if ((h >= 0 && h < 15) || h >= 345) return 'Red';
  if (h >= 15 && h < 45) return 'Orange';
  if (h >= 45 && h < 70) return 'Yellow';
  if (h >= 70 && h < 170) return 'Green';
  if (h >= 170 && h < 200) return 'Teal';
  if (h >= 200 && h < 260) return 'Blue';
  if (h >= 260 && h < 290) return 'Purple';
  if (h >= 290 && h < 345) return 'Pink';
  return 'Color';
};

// Format numeric values (assumed scale 0..1000 => 0%..100%) to one decimal place
const formatNumericAsPercent = (v, scale = 1000) => {
  const n = Number(v);
  if (!isFinite(n)) return null;
  const pct = (n / scale) * 100;
  // clamp between 0 and 100 for display sanity
  const clamped = Math.max(0, Math.min(100, pct));
  return `${clamped.toFixed(1)}%`;
};

// Define the desired order for health metrics
const getHealthMetricOrder = (key) => {
  const lowerKey = key.toLowerCase();
  if (lowerKey.includes('overall')) return 1;
  if (lowerKey.includes('physical')) return 2;
  if (lowerKey.includes('mental')) return 3;
  if (lowerKey.includes('clinical')) return 4;
  if (lowerKey.includes('lifestyle')) return 5;
  // Health recommendations and other items come after health metrics
  if (lowerKey.includes('recommendation') || lowerKey.includes('insight')) return 6;
  return 7; // Everything else comes last
};

// Component to render detailed recommendations
const DetailedRecommendations = ({ recommendations }) => {
  if (!recommendations || !recommendations.detailedRecommendations) {
    return null;
  }

  const { detailedRecommendations } = recommendations;

  return (
    <div className="detailed-recommendations mt-3">
      <h6 className="text-primary mb-3">Detailed Health Recommendations</h6>

      {/* Priority Areas */}
      {detailedRecommendations.summary && detailedRecommendations.summary.priority_areas && (
        <div className="priority-areas mb-3">
          <h7 className="text-warning">Priority Areas:</h7>
          <div className="ms-2">
            {detailedRecommendations.summary.priority_areas.map((area, index) => (
              <span key={index} className="badge bg-warning text-dark me-1 mb-1">
                {area}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Immediate Actions */}
      {detailedRecommendations.summary && detailedRecommendations.summary.immediate_actions_required && (
        <div className="immediate-actions mb-3">
          <h7 className="text-danger">Immediate Actions Required:</h7>
          <ul className="ms-2 mb-0">
            {detailedRecommendations.summary.immediate_actions_required.map((action, index) => (
              <li key={index} className="text-danger">{action}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Stress Management */}
      {detailedRecommendations.stress_management && (
        <div className="recommendation-section mb-3">
          <h7 className="text-info">Stress Management:</h7>
          <div className="ms-2">
            <div><strong>Level:</strong> {detailedRecommendations.stress_management.current_level === 'Never' || detailedRecommendations.stress_management.current_level === '0' || detailedRecommendations.stress_management.current_level === 0 ? 'Never' :
              detailedRecommendations.stress_management.current_level === 'Rarely' || detailedRecommendations.stress_management.current_level === 'Rare' || detailedRecommendations.stress_management.current_level === '1' || detailedRecommendations.stress_management.current_level === 1 ? 'Sometimes' :
              detailedRecommendations.stress_management.current_level === 'Sometimes' || detailedRecommendations.stress_management.current_level === '2' || detailedRecommendations.stress_management.current_level === 2 ? 'Many times' :
              detailedRecommendations.stress_management.current_level === 'Often' || detailedRecommendations.stress_management.current_level === 'Very Often' || detailedRecommendations.stress_management.current_level === '3' || detailedRecommendations.stress_management.current_level === 3 ? 'Always' :
              detailedRecommendations.stress_management.current_level} ({detailedRecommendations.stress_management.risk_category})</div>
            {detailedRecommendations.stress_management.immediate_actions && (
              <div>
                <strong>Actions:</strong>
                <ul className="mb-0">
                  {detailedRecommendations.stress_management.immediate_actions.map((action, index) => (
                    <li key={index}>{action}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Blood Pressure */}
      {detailedRecommendations.blood_pressure && (
        <div className="recommendation-section mb-3">
          <h7 className="text-success">Blood Pressure Management:</h7>
          <div className="ms-2">
            <div><strong>Category:</strong> {detailedRecommendations.blood_pressure.current_category} ({detailedRecommendations.blood_pressure.risk_level})</div>
            {detailedRecommendations.blood_pressure.immediate_actions && (
              <div>
                <strong>Actions:</strong>
                <ul className="mb-0">
                  {detailedRecommendations.blood_pressure.immediate_actions.map((action, index) => (
                    <li key={index}>{action}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Heart Rate */}
      {detailedRecommendations.heart_rate && (
        <div className="recommendation-section mb-3">
          <h7 className="text-secondary">Heart Rate & Fitness:</h7>
          <div className="ms-2">
            <div><strong>Level:</strong> {detailedRecommendations.heart_rate.current_level} ({detailedRecommendations.heart_rate.fitness_indicator})</div>
            {detailedRecommendations.heart_rate.immediate_actions && (
              <div>
                <strong>Actions:</strong>
                <ul className="mb-0">
                  {detailedRecommendations.heart_rate.immediate_actions.map((action, index) => (
                    <li key={index}>{action}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Daily Routine */}
      {detailedRecommendations.integrated_plan && detailedRecommendations.integrated_plan.daily_routine && (
        <div className="daily-routine">
          <h7 className="text-primary">Recommended Daily Routine:</h7>
          <ul className="ms-2 mb-0">
            {detailedRecommendations.integrated_plan.daily_routine.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const AIAssessmentViewer = ({ data }) => {
  if (!data) return <div>No AI Assessment available.</div>;

  // Check if we have detailed recommendations
  const hasDetailedRecommendations = data.detailedRecommendations;

  // Sort the data entries according to the desired health metric order
  const sortedEntries = Object.entries(data).sort(([keyA], [keyB]) => {
    const orderA = getHealthMetricOrder(keyA);
    const orderB = getHealthMetricOrder(keyB);

    if (orderA !== orderB) {
      return orderA - orderB;
    }

    // If same order priority, maintain alphabetical order within the group
    return keyA.localeCompare(keyB);
  });

  return (
    <div className="card border-0 shadow-sm mb-3" style={{ background: "#f5f5f5" }}>
      {/* <div className="card-header bg-info text-white">
        <h5 className="mb-0">AI Assessment Summary</h5>
      </div> */}
      <div className="card-body" style={{ fontSize: '13px' }}>
        {/* Show detailed recommendations first if available */}
        {hasDetailedRecommendations && (
          <DetailedRecommendations recommendations={data} />
        )}

        {/* Show original health metrics */}
        <div className="mt-3">
          <h6 className="text-muted mb-3">Health Metrics</h6>
          {sortedEntries.map(([key, value]) => {
            // Skip detailedRecommendations as we handle it separately
            if (key === 'detailedRecommendations') return null;

            const displayKey = mapAIKey(key);
            // If the value is a simple string, map known abbreviations
            let displayValue = typeof value === 'string' ? mapAIValue(value) : value;

            // If it's numeric (number or numeric string), convert to percent out of 1000
            if (typeof value === 'number') {
              const p = formatNumericAsPercent(value);
              if (p !== null) displayValue = p;
            } else if (typeof displayValue === 'string' && displayValue.trim() !== '') {
              // avoid treating color codes or mapped text as numbers
              const maybeNum = Number(displayValue);
              if (!isNaN(maybeNum) && isFinite(maybeNum)) {
                const p = formatNumericAsPercent(maybeNum);
                if (p !== null) displayValue = p;
              }
            }

            return (
              <div className="row mb-2" key={key}>
                <div className="col-5 text-muted fw-bold">{displayKey}:</div>
                <div className="col-7">
                  {isColorCode(displayValue) ? (
                    <div
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '180px',
                        maxWidth: '100%',
                        height: 34,
                        backgroundColor: displayValue,
                        border: '1px solid rgba(0,0,0,0.08)',
                        borderRadius: 6,
                        verticalAlign: 'middle',
                        padding: '0 8px',
                        boxSizing: 'border-box'
                      }}
                    >
                      <span style={{ color: contrastTextColor(displayValue), fontWeight: 600 }}>{labelForColor(displayValue)}</span>
                    </div>
                  ) : typeof displayValue === 'object' && displayValue !== null ? (
                    <pre className="bg-light rounded p-2">{JSON.stringify(displayValue, null, 2)}</pre>
                  ) : (
                    <span>{String(displayValue)}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AIAssessmentViewer;